/* eslint-disable import/no-mutable-exports */
import { REEBELO_CURRENCY } from '@lambda/reebelo';
import config from './configs';

export default config;

if (
  process.env.STORE == null &&
  process.env.NEXT_PUBLIC_STORE == null &&
  process.env.NODE_ENV !== 'test'
) {
  throw Error(
    `STORE is not defined: ${process.env.STORE} - ${process.env.NEXT_PUBLIC_STORE}`,
  );
}

if (process.env.NODE_ENV === 'test') {
  // add env in test
  // eslint-disable-next-line
  const env = require('../scripts/env');

  process.env = { ...process.env, ...env };
}

// Intl locale used to format currency, per store. Existing stores stay on
// 'en-US' because the symbol fix-ups in `short`/`long` below depend on en-US
// output. Euro stores use a European locale so the symbol trails the amount
// with a space and the decimal separator is a comma (e.g. "1 234,50 €" rather
// than "€1,234.50").
const CURRENCY_LOCALE: Record<string, string> = {
  'reebelo-fr': 'fr-FR',
  'reebelo-de': 'de-DE',
  'reebelo-es': 'es-ES',
  'reebelo-it': 'it-IT',
  // SEK: "1 299,00 kr" (non-breaking-space grouping, comma decimal, trailing kr).
  'reebelo-se': 'sv-SE',
  // EUR with English copy: "€1,299.00" (period decimal, comma grouping).
  'reebelo-ie': 'en-IE',
  'reebelo-nl': 'nl-NL',
  // Same EUR format as reebelo-de ("1.299,00 €"); de-AT would put the symbol first ("€ 1.299,00").
  'reebelo-at': 'de-DE',
  'reebelo-be': 'fr-BE',
  'reebelo-pt': 'pt-PT',
  'reebelo-fi': 'fi-FI',
};

const currencyLocale = CURRENCY_LOCALE[config.store] ?? 'en-US';

/**
 * The store's Intl locale for dates and numbers on the European stores (the
 * same map as currency), null elsewhere: those stores keep their existing
 * formats. See `lib/intl-format.ts`.
 */
export const storeIntlLocale: string | null =
  CURRENCY_LOCALE[config.store] ?? null;

export const priceFormater = {
  getCurrencySymbol: () => {
    const parts = new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currency: REEBELO_CURRENCY[config.store],
      currencyDisplay: 'symbol',
    }).formatToParts(0);

    return parts.find((p) => p.type === 'currency')?.value ?? '$';
  },
  // The decimal separator this store's locale uses: "." in en-US, "," in
  // de-DE/fr-FR/es-ES/it-IT. Anything that splits a formatted price needs it —
  // in de-DE "1.047,99 €" the dot groups thousands, so a hard-coded "." cuts
  // the number in the wrong place.
  getDecimalSeparator: () => {
    const parts = new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currency: REEBELO_CURRENCY[config.store],
      minimumFractionDigits: 2,
    }).formatToParts(1.5);

    return parts.find((p) => p.type === 'decimal')?.value ?? '.';
  },
  // Whether the currency symbol comes after the amount in this store's locale
  // (e.g. "1,00 €" in fr-FR) rather than before it ("$1.00" in en-US). Used by
  // UIs that place the symbol themselves, such as the price-range filter.
  isCurrencySymbolTrailing: () => {
    const parts = new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currency: REEBELO_CURRENCY[config.store],
      currencyDisplay: 'symbol',
    }).formatToParts(1);

    const currencyIndex = parts.findIndex((p) => p.type === 'currency');
    const numberIndex = parts.findIndex((p) =>
      ['integer', 'decimal', 'fraction'].includes(p.type),
    );

    return currencyIndex > numberIndex;
  },
  short: (
    number: number | string,
    showDecimals = true,
    showCurrencySymbol = true,
  ) => {
    const value = new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currencyDisplay: 'symbol', // note that Safari does not support 'symbolCode'
      currency: REEBELO_CURRENCY[config.store],
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0,
    }).format(typeof number === 'number' ? number : Number(number));

    const formattedValue = value.replace('SGD ', '$').replace('NZ$', '$');

    return showCurrencySymbol
      ? formattedValue
      : formattedValue.replace(priceFormater.getCurrencySymbol(), '').trim();
  },

  long: (number: number | string) => {
    const value = new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currencyDisplay: 'symbol', // Shows A$
      currency: REEBELO_CURRENCY[config.store],
    }).format(typeof number === 'number' ? number : Number(number));

    if (config.store === 'reebelo-au') {
      return value.replace('$A', '$AU');
    }

    return value;
  },
  code: (number: number | string) =>
    new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currencyDisplay: 'code', // Shows AUD
      currency: REEBELO_CURRENCY[config.store],
    }).format(typeof number === 'number' ? number : Number(number)),
};
